import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Car, BookOpen, MessageSquare, LayoutDashboard, LogOut, Menu, X, Upload, Image, Trash2, Users, Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eurocar-start/artifacts/io6ga27q_eurocars-logo2.png";

function AdminLayout({ children }) {
  const { admin, logout, loading, authHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login");
  }, [admin, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!admin) return null;

  const links = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/admin/cars", icon: Car, label: "Cars" },
    { to: "/admin/bookings", icon: BookOpen, label: "Bookings" },
    { to: "/admin/messages", icon: MessageSquare, label: "Messages" },
    { to: "/admin/users", icon: Users, label: "Users" },  // ✅ NEW
    { to: "/admin/pricing", icon: Calendar, label: "Calendar" },  // ✅ NEW
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-slate-900 flex-col fixed inset-y-0 left-0 z-30">
        <div className="p-6 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Euro Cars" className="h-10 w-auto object-contain" />
          </Link>
          <p className="font-dm-sans text-xs text-slate-400 mt-2">Admin Panel</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-outfit text-sm font-medium transition-all ${
                location.pathname === link.to ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20" : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            data-testid="admin-logout-btn"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl font-outfit text-sm font-medium text-red-400 hover:bg-red-900/20 transition-all"
          >
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 h-14">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <span className="font-outfit font-bold text-sm text-white">Admin Panel</span>
        <button onClick={() => { logout(); navigate("/admin/login"); }} className="text-red-400">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setSidebarOpen(false)}>
          <aside className="w-64 bg-slate-900 h-full p-4 pt-16 space-y-1" onClick={(e) => e.stopPropagation()}>
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl font-outfit text-sm font-medium transition-all ${
                  location.pathname === link.to ? "bg-orange-500 text-white" : "text-slate-400 hover:bg-slate-800"
                }`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}
          </aside>
        </div>
      )}

      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 md:p-8">{children}</div>
      </main>
    </div>
  );
}

// ─── Dashboard ───
export function AdminDashboard() {
  const { authHeaders } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: authHeaders }).then((res) => setStats(res.data)).catch(() => {});
  }, [authHeaders]);

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <h1 className="font-outfit font-bold text-2xl md:text-3xl text-slate-900 mb-6">Dashboard</h1>
        {stats ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Total Cars", value: stats.total_cars, color: "bg-blue-600" },
              { label: "Total Bookings", value: stats.total_bookings, color: "bg-green-600" },
              { label: "Pending Bookings", value: stats.pending_bookings, color: "bg-orange-500" },
              { label: "Unread Messages", value: stats.unread_contacts, color: "bg-red-500" },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
                <p className="font-dm-sans text-sm text-slate-500 mb-1">{item.label}</p>
                <p className="font-outfit font-bold text-3xl text-slate-900">{item.value}</p>
                <div className={`h-1 w-12 ${item.color} rounded-full mt-3`} />
              </div>
            ))}
          </div>
        ) : (
          <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map((i) => <div key={i} className="bg-white rounded-xl p-6 border border-slate-100 h-28" />)}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ─── Cars Management ───
export function AdminCars() {
  const { authHeaders } = useAuth();
  const [cars, setCars] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    brand: "", model: "", year: 2024, mileage: 0, engine_cc: 1000, horsepower: 100,
    fuel_type: "gasoline", transmission: "manual", seats: 5, features: "", category: "economy",
    price_per_day: 30, sale_price: 0, listing_type: "rent", image_url: "", images: [], available: true,
  });

  const loadCars = () => axios.get(`${API}/cars?show_all=true`).then((res) => setCars(res.data)).catch(() => {});
  useEffect(() => { loadCars(); }, []);

  const resetForm = () => {
    setForm({ brand: "", model: "", year: 2024, mileage: 0, engine_cc: 1000, horsepower: 100, fuel_type: "gasoline", transmission: "manual", seats: 5, features: "", category: "economy", price_per_day: 30, sale_price: 0, listing_type: "rent", image_url: "", images: [], available: true });
    setEditing(null);
    setUploadedImages([]);
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
        });
        const newUrl = res.data.url;
        setUploadedImages((prev) => [...prev, newUrl]);
        setForm((prev) => ({
          ...prev,
          image_url: prev.image_url || newUrl,
          images: [...prev.images, newUrl],
        }));
        toast.success(`Uploaded ${file.name}`);
      }
    } catch { toast.error("Upload failed"); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== idx));
    setForm((prev) => {
      const imgs = prev.images.filter((_, i) => i !== idx);
      return { ...prev, images: imgs, image_url: imgs[0] || "" };
    });
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const newImages = [...uploadedImages];
    const [moved] = newImages.splice(dragIdx, 1);
    newImages.splice(targetIdx, 0, moved);
    setUploadedImages(newImages);
    setForm((prev) => ({
      ...prev,
      images: newImages,
      image_url: newImages[0] || ""
    }));
    setDragIdx(null);
  };

  const handleSave = async () => {
    if (!form.brand || !form.model) { toast.error("Brand and Model are required"); return; }
    try {
      const payload = {
        ...form,
        images: uploadedImages,
        image_url: uploadedImages[0] || form.image_url || ""
      };
      if (editing) await axios.put(`${API}/cars/${editing}`, payload, { headers: authHeaders });
      else await axios.post(`${API}/cars`, payload, { headers: authHeaders });
      resetForm(); loadCars();
      toast.success(editing ? "Car updated" : "Car added");
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this car?")) return;
    await axios.delete(`${API}/cars/${id}`, { headers: authHeaders });
    loadCars(); toast.success("Car deleted");
  };

  const toggleAvailable = async (car) => {
    try {
      const res = await axios.put(`${API}/cars/${car.id}/toggle`, {}, { headers: authHeaders });
      setCars((prev) => prev.map((c) => c.id === car.id ? { ...c, available: res.data.available } : c));
      toast.success(res.data.available ? "Car enabled" : "Car disabled");
    } catch { toast.error("Toggle failed"); }
  };

  const startEdit = (car) => {
    setEditing(car.id);
    const allImages = [car.image_url, ...(car.images || [])].filter(Boolean);
    const uniqueImages = [...new Set(allImages)];
    setForm({
      brand: car.brand, model: car.model, year: car.year, mileage: car.mileage, engine_cc: car.engine_cc,
      horsepower: car.horsepower, fuel_type: car.fuel_type, transmission: car.transmission, seats: car.seats,
      features: car.features, category: car.category, price_per_day: car.price_per_day,
      sale_price: car.sale_price || 0, listing_type: car.listing_type || "rent",
      image_url: uniqueImages[0] || "", images: uniqueImages, available: car.available,
    });
    setUploadedImages(uniqueImages);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-cars">
        <h1 className="font-outfit font-bold text-2xl md:text-3xl text-slate-900 mb-6">Cars Management</h1>

        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm mb-8">
          <h3 className="font-outfit font-semibold text-lg mb-4">{editing ? "Edit Car" : "Add New Car"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Brand *</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none" placeholder="e.g. BMW" value={form.brand} onChange={(e) => setForm({...form, brand: e.target.value})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Model *</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none" placeholder="e.g. X5 M50d" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Year</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" type="number" placeholder="e.g. 2024" value={form.year} onChange={(e) => setForm({...form, year: parseInt(e.target.value) || 0})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Listing Type</label><select className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:border-blue-400 outline-none" value={form.listing_type} onChange={(e) => setForm({...form, listing_type: e.target.value})}><option value="rent">For Rent</option><option value="sale">For Sale</option><option value="both">Rent & Sale</option></select></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Rent Price / Day (€)</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" type="number" placeholder="e.g. 50" value={form.price_per_day} onChange={(e) => setForm({...form, price_per_day: parseFloat(e.target.value) || 0})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Sale Price (€)</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" type="number" placeholder="e.g. 25000" value={form.sale_price} onChange={(e) => setForm({...form, sale_price: parseFloat(e.target.value) || 0})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Mileage (km)</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" type="number" placeholder="e.g. 45000" value={form.mileage} onChange={(e) => setForm({...form, mileage: parseInt(e.target.value) || 0})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Engine (cc)</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" type="number" placeholder="e.g. 1998" value={form.engine_cc} onChange={(e) => setForm({...form, engine_cc: parseInt(e.target.value) || 0})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Horsepower (HP)</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" type="number" placeholder="e.g. 252" value={form.horsepower} onChange={(e) => setForm({...form, horsepower: parseInt(e.target.value) || 0})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Seats</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" type="number" placeholder="e.g. 5" value={form.seats} onChange={(e) => setForm({...form, seats: parseInt(e.target.value) || 5})} /></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Fuel Type</label><select className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:border-blue-400 outline-none" value={form.fuel_type} onChange={(e) => setForm({...form, fuel_type: e.target.value})}><option value="gasoline">Gasoline</option><option value="diesel">Diesel</option><option value="hybrid">Hybrid</option><option value="lpg">LPG</option></select></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Transmission</label><select className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:border-blue-400 outline-none" value={form.transmission} onChange={(e) => setForm({...form, transmission: e.target.value})}><option value="manual">Manual</option><option value="automatic">Automatic</option></select></div>
            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Category</label><select className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:border-blue-400 outline-none" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}><option value="economy">Economy</option><option value="compact">Compact</option><option value="suv">SUV</option><option value="family">Family</option><option value="premium">Premium</option><option value="luxury">Luxury</option><option value="van">Van</option><option value="minibus">Mini Bus</option></select></div>            <div><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Main Image URL</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" placeholder="https://..." value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} /></div>
            <div className="md:col-span-2"><label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Features & Extras</label><input className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none" placeholder="e.g. Panorama, Leather, Navigation" value={form.features} onChange={(e) => setForm({...form, features: e.target.value})} /></div>
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <Image className="w-4 h-4 text-slate-500" />
              <span className="font-outfit text-sm font-medium text-slate-700">Gallery Photos</span>
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-image-btn" className="flex items-center gap-1.5 h-8 px-4 bg-blue-50 text-blue-600 rounded-lg font-outfit text-xs font-medium hover:bg-blue-100 transition-colors"><Upload className="w-3.5 h-3.5" />{uploading ? "Uploading..." : "Upload Photos"}</button>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
              {uploadedImages.length > 1 && <span className="font-dm-sans text-[11px] text-slate-400 italic">Drag to reorder. First = main image.</span>}
            </div>
            {uploadedImages.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="gallery-reorder">
                {uploadedImages.map((img, i) => (
                  <div key={`${img}-${i}`} draggable onDragStart={() => handleDragStart(i)} onDragOver={handleDragOver} onDrop={() => handleDrop(i)} className={`relative w-24 h-16 rounded-lg overflow-hidden border-2 group cursor-grab active:cursor-grabbing transition-all ${i === 0 ? "border-orange-500 ring-2 ring-orange-200" : "border-slate-200"} ${dragIdx === i ? "opacity-40 scale-95" : ""}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'/>"; }} />
                    {i === 0 && <span className="absolute top-0.5 left-0.5 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">MAIN</span>}
                    <button onClick={(e) => { e.stopPropagation(); removeImage(i); }} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3 text-white" /></button>
                    <span className="absolute bottom-0.5 right-0.5 bg-black/60 text-white text-[9px] px-1 rounded">{i + 1}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 mt-5">
            <button onClick={handleSave} data-testid="save-car-btn" className="h-10 px-6 bg-blue-600 text-white rounded-lg font-outfit text-sm font-medium hover:bg-blue-700 transition-colors">{editing ? "Update Car" : "Add Car"}</button>
            {editing && <button onClick={resetForm} className="h-10 px-4 border border-slate-200 rounded-lg font-outfit text-sm text-slate-500 hover:bg-slate-50">Cancel</button>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 bg-slate-50">
              <th className="text-left p-3 font-outfit font-medium text-slate-500">Car</th>
              <th className="text-left p-3 font-outfit font-medium text-slate-500">Type</th>
              <th className="text-left p-3 font-outfit font-medium text-slate-500">Price</th>
              <th className="text-left p-3 font-outfit font-medium text-slate-500">Fuel</th>
              <th className="text-left p-3 font-outfit font-medium text-slate-500">Imgs</th>
              <th className="text-center p-3 font-outfit font-medium text-slate-500">Visible</th>
              <th className="text-left p-3 font-outfit font-medium text-slate-500">Actions</th>
            </tr></thead>
            <tbody>
              {cars.map((car) => (
                <tr key={car.id} className={`border-b border-slate-50 transition-colors ${!car.available ? "bg-slate-50/50 opacity-60" : ""}`}>
                  <td className="p-3"><div className="flex items-center gap-3"><img src={car.image_url} alt="" className="w-12 h-8 object-cover rounded" onError={(e) => { e.target.style.display = 'none'; }} /><div><p className="font-outfit font-medium text-slate-900">{car.brand} {car.model}</p><p className="text-xs text-slate-400">{car.year} · {car.category}</p></div></div></td>
                  <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${car.listing_type === "sale" ? "bg-emerald-50 text-emerald-700" : car.listing_type === "both" ? "bg-purple-50 text-purple-700" : "bg-blue-50 text-blue-700"}`}>{car.listing_type === "both" ? "Rent & Sale" : car.listing_type === "sale" ? "For Sale" : "For Rent"}</span></td>
                  <td className="p-3"><p className="font-medium text-slate-900">€{car.price_per_day}/day</p>{(car.listing_type === "sale" || car.listing_type === "both") && car.sale_price > 0 && <p className="text-xs text-emerald-600">€{car.sale_price?.toLocaleString()}</p>}</td>
                  <td className="p-3 capitalize text-slate-600">{car.fuel_type}</td>
                  <td className="p-3 text-slate-600"><span className="inline-flex items-center gap-1"><Image className="w-3.5 h-3.5" />{1 + (car.images?.length || 0)}</span></td>
                  <td className="p-3 text-center"><button onClick={() => toggleAvailable(car)} data-testid={`toggle-car-${car.id}`} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${car.available ? "bg-blue-600" : "bg-slate-300"}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${car.available ? "translate-x-6" : "translate-x-1"}`} /></button></td>
                  <td className="p-3"><div className="flex gap-2"><button onClick={() => startEdit(car)} data-testid={`edit-car-${car.id}`} className="text-blue-600 hover:underline text-xs font-medium">Edit</button><button onClick={() => handleDelete(car.id)} data-testid={`delete-car-${car.id}`} className="text-red-500 hover:underline text-xs font-medium">Delete</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

// ─── Bookings Management ───
export function AdminBookings() {
  const { authHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);

  const loadBookings = () => axios.get(`${API}/bookings`, { headers: authHeaders }).then((res) => setBookings(res.data)).catch(() => {});
  useEffect(() => { loadBookings(); }, [authHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/bookings/${id}?status=${status}`, {}, { headers: authHeaders });
    loadBookings();
  };

  const statusColors = { pending: "bg-yellow-100 text-yellow-700", confirmed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700", completed: "bg-blue-100 text-blue-700" };

  return (
    <AdminLayout>
      <div data-testid="admin-bookings">
        <h1 className="font-outfit font-bold text-2xl md:text-3xl text-slate-900 mb-6">Bookings</h1>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left p-3 font-outfit font-medium text-slate-500">Customer</th>
                <th className="text-left p-3 font-outfit font-medium text-slate-500">Car</th>
                <th className="text-left p-3 font-outfit font-medium text-slate-500">Dates</th>
                <th className="text-left p-3 font-outfit font-medium text-slate-500">Total</th>
                <th className="text-left p-3 font-outfit font-medium text-slate-500">Status</th>
                <th className="text-left p-3 font-outfit font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-slate-50">
                  <td className="p-3">
                    <p className="font-medium text-slate-900">{b.customer_name}</p>
                    <p className="text-xs text-slate-400">{b.customer_email}</p>
                    <p className="text-xs text-slate-400">{b.customer_phone}</p>
                  </td>
                  <td className="p-3 text-slate-700">{b.car_name}</td>
                  <td className="p-3 text-slate-600 text-xs">{b.pickup_date} to {b.return_date}<br/><span className="text-slate-400">{b.pickup_location}</span></td>
                  <td className="p-3 font-medium">€{b.total_price}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[b.status] || "bg-slate-100 text-slate-600"}`}>
                      {b.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <select
                      data-testid={`booking-status-${b.id}`}
                      value={b.status}
                      onChange={(e) => updateStatus(b.id, e.target.value)}
                      className="text-xs border border-slate-200 rounded px-2 py-1"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-slate-400">No bookings yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

// ─── Messages ───
export function AdminMessages() {
  const { authHeaders } = useAuth();
  const [contacts, setContacts] = useState([]);

  const loadContacts = () => axios.get(`${API}/contacts`, { headers: authHeaders }).then((res) => setContacts(res.data)).catch(() => {});
  useEffect(() => { loadContacts(); }, [authHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (id) => {
    await axios.put(`${API}/contacts/${id}/read`, {}, { headers: authHeaders });
    loadContacts();
  };

  const deleteContact = async (id) => {
    await axios.delete(`${API}/contacts/${id}`, { headers: authHeaders });
    loadContacts();
  };

  return (
    <AdminLayout>
      <div data-testid="admin-messages">
        <h1 className="font-outfit font-bold text-2xl md:text-3xl text-slate-900 mb-6">Contact Messages</h1>
        <div className="space-y-4">
          {contacts.map((c) => (
            <div key={c.id} className={`bg-white rounded-xl p-5 border shadow-sm transition-all ${c.read ? "border-slate-100" : "border-blue-200 bg-blue-50/30"}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-outfit font-medium text-slate-900">{c.name}</h4>
                    {!c.read && <span className="w-2 h-2 bg-blue-600 rounded-full" />}
                  </div>
                  <p className="font-dm-sans text-xs text-slate-400">{c.email} {c.phone && `| ${c.phone}`}</p>
                  {c.subject && <p className="font-outfit text-sm font-medium text-slate-700 mt-2">{c.subject}</p>}
                  <p className="font-dm-sans text-sm text-slate-600 mt-1">{c.message}</p>
                  <p className="font-dm-sans text-xs text-slate-400 mt-2">{new Date(c.created_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {!c.read && (
                    <button onClick={() => markRead(c.id)} data-testid={`mark-read-${c.id}`} className="text-blue-600 hover:underline text-xs font-medium">Mark Read</button>
                  )}
                  <button onClick={() => deleteContact(c.id)} data-testid={`delete-msg-${c.id}`} className="text-red-500 hover:underline text-xs font-medium">Delete</button>
                </div>
              </div>
            </div>
          ))}
          {contacts.length === 0 && (
            <div className="text-center py-12 text-slate-400">No messages yet</div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// ─── ✅ NEW: User Management ───
export function AdminUsers() {
  const { authHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ username: "", password: "", role: "admin" });
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: authHeaders });
      setUsers(res.data);
    } catch {
      // Fallback: show current admin from token
      try {
        const res = await axios.get(`${API}/admin/me`, { headers: authHeaders });
        setUsers([{ username: res.data.username, role: res.data.role, id: "current" }]);
      } catch {}
    }
  };

  useEffect(() => { loadUsers(); }, [authHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setForm({ username: "", password: "", role: "admin" });
    setEditingUser(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.username || (!editingUser && !form.password)) {
      toast.error("Username and password are required");
      return;
    }
    setSaving(true);
    try {
      if (editingUser) {
        await axios.put(`${API}/admin/users/${editingUser}`, form, { headers: authHeaders });
        toast.success("User updated");
      } else {
        await axios.post(`${API}/admin/users`, form, { headers: authHeaders });
        toast.success("User created");
      }
      resetForm();
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${username}`, { headers: authHeaders });
      toast.success("User deleted");
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const startEdit = (user) => {
    setEditingUser(user.username);
    setForm({ username: user.username, password: "", role: user.role || "admin" });
    setShowForm(true);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-users">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-outfit font-bold text-2xl md:text-3xl text-slate-900">Users</h1>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            data-testid="add-user-btn"
            className="flex items-center gap-2 h-10 px-5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-outfit text-sm font-medium transition-colors shadow-lg shadow-orange-500/20"
          >
            <Users className="w-4 h-4" />
            + Add User
          </button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm mb-6">
            <h3 className="font-outfit font-semibold text-base mb-4">{editingUser ? "Edit User" : "New Admin User"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Username *</label>
                <input
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none"
                  placeholder="e.g. manager"
                  value={form.username}
                  disabled={!!editingUser}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                />
              </div>
              <div>
                <label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">
                  Password {editingUser ? "(leave blank to keep)" : "*"}
                </label>
                <input
                  type="password"
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-blue-400 outline-none"
                  placeholder={editingUser ? "New password (optional)" : "Password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
              <div>
                <label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">Role</label>
                <select
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:border-blue-400 outline-none"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-outfit text-sm font-medium transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : editingUser ? "Update User" : "Create User"}
              </button>
              <button onClick={resetForm} className="h-10 px-4 border border-slate-200 rounded-lg font-outfit text-sm text-slate-500 hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left p-4 font-outfit font-medium text-slate-500">User</th>
                <th className="text-left p-4 font-outfit font-medium text-slate-500">Role</th>
                <th className="text-left p-4 font-outfit font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.username || user.id} className="border-b border-slate-50 last:border-0">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="font-outfit font-bold text-sm text-orange-600">
                          {(user.username || "?").slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <span className="font-outfit font-medium text-slate-900">{user.username}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-outfit font-medium capitalize ${
                      user.role === "admin" ? "bg-orange-50 text-orange-700" : "bg-blue-50 text-blue-700"
                    }`}>
                      {user.role || "admin"}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-3">
                      <button
                        onClick={() => startEdit(user)}
                        data-testid={`edit-user-${user.username}`}
                        className="text-blue-600 hover:underline text-xs font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.username)}
                        data-testid={`delete-user-${user.username}`}
                        className="text-red-500 hover:underline text-xs font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan="3" className="p-8 text-center text-slate-400">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
